/**
 * Shared types, constants, and pure helpers for the proposed-edits pages.
 * No React components / JSX in this file — those live in
 * `_shared-components.tsx`. Splitting along that line keeps both files
 * Fast-Refresh-friendly per the repo eslint rule.
 *
 * Extracted from the original monolithic `list.tsx` so the inline
 * `ProposedEditsPanel` (on funder/opportunity show pages) and the
 * full-page `ProposedEditsList` can share the same logic.
 */

import axios from 'axios'

import { StructuredQuestion } from '../../interfaces/questions'
import {
  AMOUNT_RANGE_KEYS,
  extractAmountRange,
  formatAmountRange,
} from './amountFields'

export const apiUrl = import.meta.env.VITE_API_URL

export type EditStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn'

/**
 * Headers for the diff table change based on whether the edit is still
 * under review or already resolved. Once approved, "Current" matches the
 * applied payload so the framing should be historical (fs-u3w).
 */
export function diffColumnLabels(status: EditStatus): {
  before: string
  after: string
} {
  if (status === 'pending') {
    return { before: 'Current', after: 'Proposed' }
  }
  return { before: 'Previous', after: 'Updated' }
}

export type ResourceType =
  | 'funder'
  | 'opportunity'
  | 'funding_round'
  | 'opportunity_questions'
  | 'advisory'
export type EditAction = 'create' | 'update' | 'delete' | 'merge'

export interface ProposedEdit {
  id: number
  resource_type: ResourceType
  action: EditAction
  target_uuid: string | null
  payload: Record<string, unknown>
  status: EditStatus
  note: string | null
  evidence_url: string
  evidence_description: string
  batch_id: string | null
  submitted_by_auth0_id: string
  submitted_by_name: string | null
  api_key_id: number | null
  created_at: string
  reviewed_at: string | null
  reviewed_by_auth0_id: string | null
  reviewed_by_name: string | null
  review_comment: string | null
  applied_target_uuid: string | null
  target_name: string | null
  target_funder_uuid: string | null
  target_funder_name: string | null
  target_opportunity_uuid: string | null
  responds_to_edit_id?: number | null
  withdraw_reason?: string | null
  withdrawn_at?: string | null
  withdrawn_by_api_key_id?: number | null
  target_round_open_date?: string | null
  target_round_close_date?: string | null
  target_round_decision_date?: string | null
  target_round_estimate_date_flag?: boolean | null
  // merge proposals: target_* describes the record being deleted, so the
  // surviving record is carried separately
  target_survivor_uuid?: string | null
  target_survivor_name?: string | null
}

export interface BatchGroup {
  batchId: string | null
  items: ProposedEdit[]
}

export function groupByBatch(items: ProposedEdit[]): BatchGroup[] {
  const result: BatchGroup[] = []
  const idx = new Map<string, number>()
  for (const item of items) {
    const b = item.batch_id
    if (b) {
      const existing = idx.get(b)
      if (existing !== undefined) {
        result[existing].items.push(item)
      } else {
        idx.set(b, result.length)
        result.push({ batchId: b, items: [item] })
      }
    } else {
      result.push({ batchId: null, items: [item] })
    }
  }
  return result
}

export interface ListResponse {
  items: ProposedEdit[]
  total: number
}

export interface FunderOption {
  uuid: string
  name: string
}

export function targetHref(item: ProposedEdit): string | null {
  if (item.resource_type === 'funding_round') {
    return item.target_opportunity_uuid
      ? `/opportunities/${item.target_opportunity_uuid}`
      : null
  }
  const uuid = item.applied_target_uuid ?? item.target_uuid
  if (!uuid) return null
  return item.resource_type === 'funder'
    ? `/funders/${uuid}`
    : `/opportunities/${uuid}`
}

/**
 * For a merge, the record the target is folded into.
 *
 * The Target column names the record being deleted, which on its own reads
 * like any other edit against it — the direction has to be shown too.
 */
export function mergeSurvivorDisplay(item: ProposedEdit): string | null {
  if (item.action !== 'merge') return null
  if (item.target_survivor_name) return item.target_survivor_name
  const uuid =
    item.target_survivor_uuid ??
    (item.payload?.survivor_uuid as string | undefined)
  return uuid ? `${uuid.slice(0, 8)}…` : null
}

export function targetDisplay(item: ProposedEdit): string {
  if (item.target_name) return item.target_name
  const uuid = item.applied_target_uuid ?? item.target_uuid
  if (uuid) return `${uuid.slice(0, 8)}…`
  return 'new'
}

export const statusColor: Record<
  EditStatus,
  'warning' | 'success' | 'error' | 'default'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  withdrawn: 'default',
}

/**
 * Visual distinction for the action column in the queue table (GH#1515).
 * Mirrors the create/update/delete chip colours used in other admin views so
 * a reviewer can scan the queue and pick out destructive vs additive edits at
 * a glance.
 */
export const actionColor: Record<
  EditAction,
  'success' | 'info' | 'error'
> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  // a merge deletes a record, so it scans as destructive alongside delete
  merge: 'error',
}

export type FieldType =
  | 'bool'
  | 'date'
  | 'int'
  | 'decimal'
  | 'text'
  | 'textarea'
  | 'url'
  // Like 'text', but clearing the input sends `null` rather than `''` — the
  // backend validates with pydantic EmailStr, which rejects `''` but treats
  // an explicit null as "clear this column" (GH#2060).
  | 'email'
  | 'select'
  // Multi-select against the regions taxonomy — `region_ids` is a list of
  // integer region ids, and a reviewer editing it needs names, not numbers.
  | 'regions'
  | 'readonly'

export interface FieldDef {
  type: FieldType
  min?: number
  max?: number
  maxLength?: number
  /**
   * For type: 'select' — the allowed values. Mirrors the dropdowns on the
   * funder/opportunity edit forms so admins can't amend an enum field with
   * arbitrary text that breaks downstream consumers (GH#1515).
   */
  options?: readonly string[]
}

/**
 * Allowed values for opportunity.flag_for_updates. Source of truth lives in
 * components/FlagOptions.ts; duplicated here so the amend dropdown stays in
 * sync without dragging the proposed-edits module into a runtime cycle with
 * components/.
 */
export const FLAG_FOR_UPDATES_OPTIONS = [
  '',
  'Active',
  'Needs Work',
  'Inactive',
] as const

/**
 * Every writable field of the backend validator models must appear in the
 * maps below — `fieldDefFor()` silently degrades an unregistered key to a
 * disabled input, which is how `region_ids` and `email_address` stayed
 * un-amendable for months (GH#2060). `__tests__/fieldSchemaContract.test.ts`
 * asserts this against a fixture dumped from the pydantic models, so a new
 * backend field cannot ship unregistered. Fields that are deliberately NOT
 * amendable are registered as `readonly` here AND listed in that test's
 * INTENTIONALLY_READONLY map with a reason.
 */
const FUNDER_FIELDS: Record<string, FieldDef> = {
  name: { type: 'text', maxLength: 255 },
  type: { type: 'text', maxLength: 255 },
  website: { type: 'url', maxLength: 2048 },
  description: { type: 'textarea' },
  notes: { type: 'textarea' },
  active: { type: 'bool' },
  status: { type: 'text', maxLength: 255 },
  next_check_in_date: { type: 'date' },
  only_funds_registered_charities: { type: 'bool' },
  gambling_proceeds: { type: 'bool' },
  is_international: { type: 'bool' },
  email_address: { type: 'email', maxLength: 255 },
  region_ids: { type: 'regions' },
  iwi: { type: 'readonly' },
  updated_by: { type: 'readonly' },
}

const OPPORTUNITY_FIELDS: Record<string, FieldDef> = {
  fund_name: { type: 'text', maxLength: 255 },
  is_relational: { type: 'bool' },
  description: { type: 'textarea' },
  criteria: { type: 'textarea' },
  exclusions: { type: 'textarea' },
  references: { type: 'textarea' },
  profile_text: { type: 'textarea' },
  timing: { type: 'textarea' },
  amount: { type: 'text' },
  min_amount: { type: 'decimal', min: 0 },
  max_amount: { type: 'decimal', min: 0 },
  no_min_max_amount: { type: 'bool' },
  notes: { type: 'textarea' },
  always_open: { type: 'bool' },
  application_info: { type: 'textarea' },
  application_link: { type: 'url', maxLength: 2048 },
  opportunity_link: { type: 'url', maxLength: 2048 },
  flag_for_updates: { type: 'select', options: FLAG_FOR_UPDATES_OPTIONS },
  next_check_in_date: { type: 'date' },
  gambling_proceeds: { type: 'bool' },
  is_international: { type: 'bool' },
  only_funds_registered_charities: { type: 'bool' },
  general_info: { type: 'textarea' },
  inherit_iwi_from_funder: { type: 'bool' },
  email_address: { type: 'email', maxLength: 255 },
  region_ids: { type: 'regions' },
  draft_profile_text: { type: 'textarea' },
  clear_next_check_in_date: { type: 'bool' },
  max_rank_is_4: { type: 'bool' },
  funder_uuid: { type: 'readonly' },
  questions: { type: 'readonly' },
  structured_questions: { type: 'readonly' },
  questions_last_checked_at: { type: 'readonly' },
  clear_questions_last_checked_at: { type: 'readonly' },
  tags: { type: 'readonly' },
  iwi: { type: 'readonly' },
  updated_by: { type: 'readonly' },
  revision_version: { type: 'readonly' },
  funding_rounds: { type: 'readonly' },
}

const ADVISORY_FIELDS: Record<string, FieldDef> = {
  message: { type: 'readonly' },
  action_required: { type: 'readonly' },
  target_kind: { type: 'readonly' },
  target_uuid: { type: 'readonly' },
  category: { type: 'readonly' },
}

const FUNDING_ROUND_FIELDS: Record<string, FieldDef> = {
  application_open_date: { type: 'date' },
  application_close_date: { type: 'date' },
  decision_date: { type: 'date' },
  estimate_date_flag: { type: 'bool' },
  opportunity_uuid: { type: 'readonly' },
  id: { type: 'readonly' },
}

export const FIELD_SCHEMAS: Record<ResourceType, Record<string, FieldDef>> = {
  funder: FUNDER_FIELDS,
  opportunity: OPPORTUNITY_FIELDS,
  funding_round: FUNDING_ROUND_FIELDS,
  // opportunity_questions never renders `AmendForm` — it has its own
  // `QuestionsAmendForm`, so FIELD_SCHEMAS is not consulted for it.
  opportunity_questions: {},
  advisory: ADVISORY_FIELDS,
}

export function fieldDefFor(resource: ResourceType, key: string): FieldDef {
  return FIELD_SCHEMAS[resource][key] ?? { type: 'readonly' }
}

export function validateField(def: FieldDef, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  if (def.type === 'bool') {
    if (typeof value !== 'boolean') return 'must be true or false'
  }
  if (def.type === 'int') {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n) || !Number.isInteger(n)) return 'must be an integer'
    if (def.min !== undefined && n < def.min) return `must be ≥ ${def.min}`
    if (def.max !== undefined && n > def.max) return `must be ≤ ${def.max}`
  }
  if (def.type === 'decimal') {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return 'must be a number'
    if (def.min !== undefined && n < def.min) return `must be ≥ ${def.min}`
    if (def.max !== undefined && n > def.max) return `must be ≤ ${def.max}`
  }
  if (def.type === 'date') {
    const s = typeof value === 'string' ? value : ''
    if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return 'must be YYYY-MM-DD'
  }
  if (
    def.type === 'text' ||
    def.type === 'textarea' ||
    def.type === 'url' ||
    def.type === 'email'
  ) {
    if (typeof value !== 'string') return 'must be text'
    if (def.maxLength && value.length > def.maxLength)
      return `max ${def.maxLength} characters`
  }
  // Catch a malformed address here rather than letting the save round-trip
  // into a pydantic EmailStr 422. Deliberately loose — the backend remains
  // the authority; this only spares the reviewer an opaque server error.
  if (def.type === 'email') {
    if (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'must be a valid email address'
    }
  }
  if (def.type === 'regions') {
    if (!Array.isArray(value)) return 'must be a list of locations'
    const bad = value.some((v) => {
      if (typeof v === 'number') return !Number.isInteger(v)
      // `Number('')` is 0, so an empty string would otherwise pass as a valid
      // region id and only fail server-side.
      if (typeof v !== 'string' || v.trim() === '') return true
      return !Number.isInteger(Number(v))
    })
    if (bad) return 'must be a list of locations'
  }
  if (def.type === 'select') {
    if (typeof value !== 'string') return 'must be text'
    if (def.options && !def.options.includes(value)) {
      return `must be one of: ${def.options.filter((o) => o !== '').join(', ')}`
    }
  }
  return null
}

export function validatePayload(
  resource: ResourceType,
  payload: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {}
  if (resource === 'opportunity_questions') {
    if (!Array.isArray(payload.questions)) {
      errors.questions = 'must be an array of structured questions'
    }
    return errors
  }
  for (const [key, value] of Object.entries(payload)) {
    const def = fieldDefFor(resource, key)
    const err = validateField(def, value)
    if (err) errors[key] = err
  }
  return errors
}

export function normalizeDate(v: unknown): string {
  if (!v) return ''
  if (typeof v !== 'string') return ''
  return v.length >= 10 ? v.slice(0, 10) : v
}

export const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  type: 'Type',
  website: 'Website',
  description: 'Description',
  notes: 'Notes',
  active: 'Active',
  status: 'Status',
  iwi: 'Iwi',
  next_check_in_date: 'Next check-in',
  only_funds_registered_charities: 'Only funds registered charities',
  gambling_proceeds: 'Gambling proceeds',
  is_international: 'International',
  region_ids: 'Locations',
  funder_uuid: 'Funder',
  fund_name: 'Fund name',
  criteria: 'Criteria',
  exclusions: 'Exclusions',
  timing: 'Timing',
  amount: 'Amount note (text)',
  min_amount: 'Min amount',
  max_amount: 'Max amount',
  questions: 'Questions',
  structured_questions: 'Structured questions',
  profile_text: 'Profile text',
  always_open: 'Always open',
  tags: 'Tags',
  flag_for_updates: 'Flag for updates',
  application_link: 'Application link',
  opportunity_link: 'Opportunity link',
  application_info: 'Application info',
  references: 'References',
  is_relational: 'Relational',
  draft_profile_text: 'Draft profile text',
  clear_next_check_in_date: 'Clear next check-in',
  inherit_iwi_from_funder: 'Inherit iwi from funder',
  estimate_date_flag: 'Dates are estimated',
  email_address: 'Email address',
  questions_last_checked_at: 'Questions last checked',
  clear_questions_last_checked_at: 'Clear questions last checked',
  max_rank_is_4: 'Max rank is 4',
  revision_version: 'Revision version',
  updated_by: 'Updated by',
  funding_rounds: 'Funding rounds',
}

export const IGNORED_DIFF_KEYS = new Set([
  'uuid',
  'id',
  'created_at',
  'updated_at',
  'funder_name',
])

export function labelFor(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, ' ')
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return value
      .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ')
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

export function regionIdsFromValue(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  const ids = value
    .map((item) => {
      if (typeof item === 'number') return item
      if (typeof item === 'string' && item.trim() !== '') return Number(item)
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>
        const id = record.region_id ?? record.id
        if (typeof id === 'number') return id
        if (typeof id === 'string' && id.trim() !== '') return Number(id)
      }
      return NaN
    })
    .filter((id) => Number.isFinite(id))
  return Array.from(new Set(ids))
}

export function formatRegionNames(
  regionIds: number[],
  regionNamesById: Map<number, string>,
): string {
  if (regionIds.length === 0) return '—'
  return regionIds
    .map((id) => regionNamesById.get(id) ?? `Unknown location (${id})`)
    .join(', ')
}

export function formatRegionValue(
  value: unknown,
  regionNamesById: Map<number, string>,
  inheritedRegionIds: number[] = [],
): string {
  const regionIds = regionIdsFromValue(value)
  if (regionIds.length > 0) return formatRegionNames(regionIds, regionNamesById)
  if (Array.isArray(value) && inheritedRegionIds.length > 0) {
    return `Inherits from funder: ${formatRegionNames(
      inheritedRegionIds,
      regionNamesById,
    )}`
  }
  return '—'
}

export type DiffSegmentType = 'equal' | 'add' | 'remove'
export interface DiffSegment {
  type: DiffSegmentType
  text: string
}

const DIFF_TOKEN_CAP = 1500

function tokenizeForDiff(s: string): string[] {
  return s.match(/\s+|\S+/g) ?? []
}

export function diffWords(before: string, after: string): DiffSegment[] | null {
  const a = tokenizeForDiff(before)
  const b = tokenizeForDiff(after)
  if (a.length > DIFF_TOKEN_CAP || b.length > DIFF_TOKEN_CAP) return null
  const n = a.length
  const m = b.length
  const dp: number[][] = []
  for (let i = 0; i <= n; i++) dp.push(new Array(m + 1).fill(0))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }
  const out: DiffSegment[] = []
  let i = n
  let j = m
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      out.unshift({ type: 'equal', text: a[i - 1] })
      i--
      j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      out.unshift({ type: 'add', text: b[j - 1] })
      j--
    } else {
      out.unshift({ type: 'remove', text: a[i - 1] })
      i--
    }
  }
  const merged: DiffSegment[] = []
  for (const seg of out) {
    const last = merged[merged.length - 1]
    if (last && last.type === seg.type) last.text += seg.text
    else merged.push({ ...seg })
  }
  return merged
}

export function isDiffableString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0
}

// C0 controls, DEL, space and backslash are exactly where the browser's WHATWG
// parser and the backend's urllib parser disagree (the browser rewrites "\" to
// "/" and strips embedded tabs); reject them before either parser sees the
// value. These two must stay character-for-character identical to the backend's
// `_URL_FORBIDDEN_RE` / `_URL_AUTHORITY_RE` in
// `app/models/proposed_edits_models.py` — see the comment there for the
// divergences the authority pattern rules out. See GH #1801.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const URL_FORBIDDEN_RE = /[\x00-\x20\x7f\\]/
const URL_AUTHORITY_RE =
  /^https?:\/\/(?:\[[0-9A-Fa-f:.]+\]|[A-Za-z0-9._~-]+)(?::[0-9]*)?(?:[/?#]|$)/i

/**
 * Canonical http(s) URL for `v`, or `null` if it is not one.
 *
 * Anything returning `null` must be rendered as inert text — the proposed-edit
 * corpus predates the ingest gate, so a stored `javascript:` value is still
 * reachable here (GH #1801).
 */
export function safeHttpUrl(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const candidate = v.trim()
  if (URL_FORBIDDEN_RE.test(candidate)) return null
  if (!URL_AUTHORITY_RE.test(candidate)) return null
  let url: URL
  try {
    url = new URL(candidate)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (!url.hostname) return null
  if (url.username || url.password) return null
  return url.href
}

export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sa = [...a].map(String).sort()
    const sb = [...b].map(String).sort()
    return sa.every((v, i) => v === sb[i])
  }
  return JSON.stringify(a) === JSON.stringify(b)
}

export interface DiffRow {
  key: string
  label: string
  before: unknown
  after: unknown
  changed: boolean
}

const FIELD_ORDER: Partial<Record<ResourceType, string[]>> = {
  funding_round: [
    'application_open_date',
    'application_close_date',
    'decision_date',
    'estimate_date_flag',
  ],
}

export function buildDiff(
  before: Record<string, unknown> | null,
  after: Record<string, unknown>,
  resource?: ResourceType,
): DiffRow[] {
  const keys = new Set<string>()
  if (before) Object.keys(before).forEach((k) => keys.add(k))
  Object.keys(after).forEach((k) => keys.add(k))
  IGNORED_DIFF_KEYS.forEach((k) => keys.delete(k))
  Array.from(keys)
    .filter((k) => k.startsWith('_'))
    .forEach((k) => keys.delete(k))

  // Determine whether any structured amount key is present in the payload.
  // Source from `after` only — checking the before+after union surfaces a
  // phantom diff row when the current record has amount fields but the
  // payload does not touch them (GH #1479).
  const amountRangeKeySet = new Set<string>(AMOUNT_RANGE_KEYS)
  const hasStructuredAmount = Object.keys(after).some((k) =>
    amountRangeKeySet.has(k),
  )

  const rows: DiffRow[] = []

  if (hasStructuredAmount) {
    // Remove individual structured amount keys — they become one synthetic row.
    amountRangeKeySet.forEach((k) => keys.delete(k))

    const beforeRange = before ? extractAmountRange(before) : {}
    const afterRange = extractAmountRange(after)
    const beforeFormatted = formatAmountRange(beforeRange)
    const afterFormatted = formatAmountRange(afterRange)
    const changed = before ? beforeFormatted !== afterFormatted : true

    rows.push({
      key: '__amount_range__',
      label: 'Amount range (structured)',
      before: beforeFormatted,
      after: afterFormatted,
      changed,
    })
  }

  keys.forEach((key) => {
    const beforeVal = before ? before[key] : undefined
    const afterVal = after[key]
    if (before) {
      if (!(key in after)) return
    }
    const changed = before ? !valuesEqual(beforeVal, afterVal) : true
    // A round update applies an explicit null to a date, so name the clear
    // rather than leaving an empty cell that reads as "no value proposed".
    // estimate_date_flag cannot be cleared; approval refuses a null there.
    const cleared =
      resource === 'funding_round' &&
      key.endsWith('_date') &&
      before !== null &&
      changed &&
      key in after &&
      afterVal === null
    rows.push({
      key,
      label: labelFor(key),
      before: beforeVal,
      after: cleared ? '(cleared)' : afterVal,
      changed,
    })
  })
  const priorityList = resource ? (FIELD_ORDER[resource] ?? []) : []
  return rows.sort((a, b) => {
    const ai = priorityList.indexOf(a.key)
    const bi = priorityList.indexOf(b.key)
    // If both rows are in the priority list, use priority order (ignores changed)
    if (ai !== -1 && bi !== -1) return ai - bi
    // If only one is in the priority list, it goes first
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    // Neither in priority list: changed rows first, then alphabetic
    if (a.changed !== b.changed) return a.changed ? -1 : 1
    return a.label.localeCompare(b.label)
  })
}

/** What a merge would do to both records, read live from the API (#1601). */
export interface MergePreview {
  loser: { uuid: string; name: string | null }
  survivor: { uuid: string; name: string | null }
  child_counts: { table: string; loser: number; survivor: number }[]
  loser_applications: { drafts: number; live: number }
  survivor_applications: { drafts: number; live: number }
  application_collisions?: {
    org_name: string | null
    org_uuid: string | null
    need_id: number | null
    loser_status: string | null
    survivor_status: string | null
    stays_live: string
  }[]
  questions_differ?: boolean
  loser_intro_letter_drafts?: number
  survivor_intro_letter_drafts?: number
  loser_opportunities?: {
    uuid: string
    fund_name: string | null
    drafts: number
    live_applications: number
    matches: number
  }[]
  survivor_opportunities?: { uuid: string; fund_name: string | null }[]
  field_conflicts: {
    field: string
    loser_value: unknown
    survivor_value: unknown
    recommended: string
  }[]
  blockers: string[]
  warnings: string[]
}

/**
 * Read what a pending merge would do.
 *
 * A live read of both records, so it only works while the merge is pending —
 * once approved the record being merged away no longer exists.
 *
 * Throws when the read fails. A merge cannot be undone, so a reviewer has to
 * be told the impact is missing rather than shown a panel that looks clear.
 */
export async function fetchMergePreview(
  edit: ProposedEdit,
): Promise<MergePreview | null> {
  const survivor =
    edit.target_survivor_uuid ?? (edit.payload?.survivor_uuid as string | undefined)
  if (!edit.target_uuid || !survivor) return null
  const path = edit.resource_type === 'funder' ? 'funders' : 'opportunities'
  const res = await axios.get(
    `${apiUrl}/fs/${path}/${edit.target_uuid}/merge-preview`,
    { params: { into: survivor } },
  )
  return res.data as MergePreview
}

/**
 * A funder merge runs an opportunity merge for every merge_into disposition,
 * and those are where the blocking conditions live — a funder preview reads
 * two funders and cannot see them. Read each pair so a reviewer learns about
 * a blocker before approving rather than from a failed approval.
 */
export async function fetchDispositionPreviews(
  edit: ProposedEdit,
): Promise<{ oppUuid: string; preview: MergePreview }[]> {
  const dispositions = edit.payload?.opp_dispositions
  if (edit.resource_type !== 'funder' || !dispositions) return []
  const pairs = Object.entries(dispositions as Record<string, unknown>)
    .map(([oppUuid, disposition]) => {
      const into =
        disposition && typeof disposition === 'object'
          ? (disposition as Record<string, unknown>).merge_into
          : null
      return into ? { oppUuid, into: String(into) } : null
    })
    .filter((pair): pair is { oppUuid: string; into: string } => pair !== null)

  // Every pair matters: one unread pair is one unseen blocker, so a partial
  // read is reported as a failure rather than shown as a shorter list.
  return await Promise.all(
    pairs.map(async ({ oppUuid, into }) => {
      const res = await axios.get(
        `${apiUrl}/fs/opportunities/${oppUuid}/merge-preview`,
        { params: { into } },
      )
      return { oppUuid, preview: res.data as MergePreview }
    }),
  )
}

export async function fetchCurrent(
  resource: ResourceType,
  uuid: string,
): Promise<Record<string, unknown> | null> {
  const path = resource === 'funder' ? 'funders' : 'opportunities'
  try {
    const res = await axios.get(`${apiUrl}/${path}/${uuid}`)
    return res.data as Record<string, unknown>
  } catch (e) {
    console.error(`Failed to fetch current ${resource}`, e)
    return null
  }
}

/**
 * For funding_round update proposals, the proposed_edits API already returns
 * the round's existing dates as `target_round_*` fields. Reuse those instead
 * of fetching — there is no GET-by-id endpoint for funding rounds, and the
 * round id is not a UUID so it cannot be passed to fetchCurrent.
 */
export function buildFundingRoundBefore(
  edit: ProposedEdit,
): Record<string, unknown> | null {
  if (
    edit.target_round_open_date == null &&
    edit.target_round_close_date == null &&
    edit.target_round_decision_date == null &&
    edit.target_round_estimate_date_flag == null
  ) {
    return null
  }
  return {
    application_open_date: edit.target_round_open_date ?? null,
    application_close_date: edit.target_round_close_date ?? null,
    decision_date: edit.target_round_decision_date ?? null,
    estimate_date_flag: edit.target_round_estimate_date_flag ?? false,
  }
}

export function questionsFromPayload(
  payload: Record<string, unknown>,
): StructuredQuestion[] {
  return Array.isArray(payload.questions)
    ? (payload.questions as StructuredQuestion[])
    : []
}
